import type { GlycerinProductClickPayload } from '@packages/tracking';
import { GlycerinPlacement } from '@packages/tracking';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';

import type { ProductCardProps } from '@packages/modules/src/ProductCard/types';
import { SearchProductCardGlycerinTrackingItemFragmentFragmentDoc } from '@packages/gql/generated/shopping/SearchProductCardGlycerinTrackingItemFragmentFragmentDoc';
import { SearchProductCardGlycerinTrackingResultFragmentFragmentDoc } from '@packages/gql/generated/shopping/SearchProductCardGlycerinTrackingResultFragmentFragmentDoc';
import { SearchProductCardGlycerinTrackingVariationGroupFragmentFragmentDoc } from '@packages/gql/generated/shopping/SearchProductCardGlycerinTrackingVariationGroupFragmentFragmentDoc';

/* GraphQL */ `
  fragment SearchProductCardGlycerinTrackingResultFragment on SearchProductResult {
    categories {
      name
      isSelected
    }
  }
`;

/* GraphQL */ `
  fragment SearchProductCardGlycerinTrackingItemFragment on StylePreviewItem {
    styleId
    styleName
    brand {
      name
    }
    listPosition
  }
`;

/* GraphQL */ `
  fragment SearchProductCardGlycerinTrackingVariationGroupFragment on CompleteVariationGroup {
    sku
  }
`;

export const buildGlycerinTrackingData = (
  maskedResult: FragmentType<typeof SearchProductCardGlycerinTrackingResultFragmentFragmentDoc>,
  maskedItem: FragmentType<typeof SearchProductCardGlycerinTrackingItemFragmentFragmentDoc>,
  maskedVariationGroup: FragmentType<
    typeof SearchProductCardGlycerinTrackingVariationGroupFragmentFragmentDoc
  >,
) => {
  const {
    styleId,
    brand: { name: brand },
    styleName: title,
    listPosition,
  } = getFragmentData(SearchProductCardGlycerinTrackingItemFragmentFragmentDoc, maskedItem);
  const { sku } = getFragmentData(
    SearchProductCardGlycerinTrackingVariationGroupFragmentFragmentDoc,
    maskedVariationGroup,
  );
  const { categories } = getFragmentData(
    SearchProductCardGlycerinTrackingResultFragmentFragmentDoc,
    maskedResult,
  );

  const currentCategoryName = categories.find((category) => category.isSelected)?.name;

  const listId = currentCategoryName || 'Search Result';

  // primaryVariationGroup.articleNumber is missing the promo code, so we need to extract it from the sku
  const articleNumberWithPromo = sku.split('-')[0];

  const clickTrackingProps: GlycerinProductClickPayload = {
    category: 'product-click',
    productId: styleId,
    articleId: articleNumberWithPromo,
    skuId: sku,
    detail: `${brand} ${title}`,
    listPosition,
    listId,
    placement: GlycerinPlacement.LISTING,
  };

  const wishListTrackingProduct: NonNullable<ProductCardProps['wishListTrackingProps']>['product'] =
    {
      productId: styleId,
      articleId: articleNumberWithPromo,
      // SKU is already automatically passed to the wishlist button for tracking from ProductCard, so no need to pass it here
      listPosition,
      origin: listId,
    };

  return {
    clickTrackingProps,
    wishListTrackingProduct,
  };
};
